import { CommonModule, DOCUMENT } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, DestroyRef, effect, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { ToastContainerComponent } from '@hunting-nature/Secacam/shared/components/toast/toast-container.component';
import { executeAfterTranslationsLoad } from '@hunting-nature/shared-core/utils/execute-after-translations-load';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';

import { GeneralActions } from './shared/redux/general/general.actions';
import { GeneralSelectors } from './shared/redux/general/general.reducer';
import { ConsentModalComponent } from './pages/menu/consent-modal/consent-modal.component';
import { WhatsNewComponent } from './pages/whats-new/whats-new.component';
import { AuthenticationService } from './shared/services/authentication.service';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [IonRouterOutlet, IonApp, CommonModule, ConsentModalComponent, WhatsNewComponent, ToastContainerComponent],
})
export class AppComponent implements OnInit {
  // services
  private readonly store = inject(Store);
  private readonly authService = inject(AuthenticationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly translocoService: TranslocoService = inject(TranslocoService);

  // private properties
  private readonly activeAppLanguage$$ = this.store.selectSignal(GeneralSelectors.activeAppLanguage);

  // UI bindings
  readonly useDarkMode$$ = this.store.selectSignal(GeneralSelectors.isUseDarkMode);
  readonly isOfflineBannerVisible$$ = this.store.selectSignal(GeneralSelectors.isOfflineBannerVisible);

  constructor() {
    this.syncRootTheme();
    executeAfterTranslationsLoad(() => this.setHtmlLanguage());
  }

  ngOnInit(): void {
    // TODO: combined them all to appInitialized action or move it to initApp$ method of general Service
    this.store.dispatch(GeneralActions.initAppTranslation());
    this.store.dispatch(GeneralActions.initAppSettings());
    this.initAuthentication();
    this.initAppListeners();
  }

  private initAuthentication(): void {
    // The startup OIDC check already ran in the app initializer; dispatch its result now that
    // effects are subscribed so downstream login effects (e.g. what's-new) still run.
    const result = this.authService.startupAuthResult;
    if (!result) {
      return;
    }
    if ('loginResponse' in result) {
      this.store.dispatch(GeneralActions.loginSucceeded({ loginResponse: result.loginResponse, isLoginCallback: result.isLoginCallback }));
    } else {
      this.store.dispatch(GeneralActions.loginFailed({ error: result.error }));
    }
  }

  private initAppListeners(): void {
    void App.addListener('appUrlOpen', (event: { url: string }) => {
      this.dispatchCheckLoggedIn(event.url);
      Browser.close().catch(() => {});
    });

    void App.addListener('resume', () => {
      this.store.dispatch(GeneralActions.appResumed({ language: this.activeAppLanguage$$() }));
    });

    const cleanup = this.authService.initCrossTabLogoutListener(() => {
      this.store.dispatch(GeneralActions.crossTabLogoutDetected());
    });
    this.destroyRef.onDestroy(cleanup);
  }

  private dispatchCheckLoggedIn(url: string | undefined): void {
    const { url: parsedUrl, isLoginCallback } = url ? this.authService.parseOidcCallbackUrl(url) : { url: undefined, isLoginCallback: false };
    this.authService.loginCallbackInProgress = isLoginCallback;
    this.store.dispatch(GeneralActions.checkLoggedIn({ url: parsedUrl, isLoginCallback }));
  }

  private syncRootTheme(): void {
    effect(() => {
      const useDarkMode = this.useDarkMode$$();
      const rootElement = this.document.documentElement;

      rootElement.classList.toggle('ion-palette-dark', useDarkMode);
      rootElement.classList.toggle('theme-dark', useDarkMode);
      rootElement.classList.toggle('theme-light', !useDarkMode);
      rootElement.style.colorScheme = useDarkMode ? 'dark' : 'light';
    });
  }

  private setHtmlLanguage(): void {
    this.document.documentElement.lang = this.translocoService.getActiveLang();
  }
}
